import { StandaloneComponent, StandaloneComponentProps } from '@/types/component';
import { mergeOptions, mergeProps } from '@/utils/merge';
import { zodResolver } from '@hookform/resolvers/zod';
import { ComponentProps, ReactNode, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { ArticlePaywallForm } from './ArticlePaywallForm';
import { ButtonProps } from '@/components/Button';

export interface ArticlePaywallFormProps extends StandaloneComponentProps<typeof ArticlePaywallForm> {
  button?: ButtonProps;
  caption?: ReactNode;
  description?: ReactNode;
  validationSchema?: z.ZodObject<any, any>;
  headline?: ReactNode;
  policy?: ReactNode;
  provider?: ReactNode;
  textFields?: ComponentProps<typeof ArticlePaywallForm.TextField>[];
  checkboxFields?: ComponentProps<typeof ArticlePaywallForm.CheckboxField>[];
  onSubmit?: (...args: any[]) => Promise<any>;
}

export const DefaultArticlePaywallFormValidationSchema = z.object({
  firstName: z
    .string()
    .nonempty('* Detta fält är obligatoriskt')
    .min(2, '* Fältet ska innehålla minst 2 tecken och högst 255 tecken')
    .max(255, '* Fältet ska innehålla minst 2 tecken och högst 255 tecken'),
  lastName: z
    .string()
    .nonempty('* Detta fält är obligatoriskt')
    .min(2, '* Fältet ska innehålla minst 2 tecken och högst 255 tecken')
    .max(255, '* Fältet ska innehålla minst 2 tecken och högst 255 tecken'),
  email: z.string().nonempty('* Var vänlig ange din e-postadress').email('* Vänligen ange en giltig e-postadress'),
});

const defaultTextFields = [
  { name: 'firstName', label: 'Förnamn' },
  { name: 'lastName', label: 'Efternamn' },
  { name: 'email', label: 'E-postadress', type: 'email' },
];

export const StandaloneArticlePaywallForm: StandaloneComponent<ArticlePaywallFormProps> = ({
  button,
  caption,
  validationSchema,
  policy,
  provider,
  textFields,
  checkboxFields,
  onSubmit,
  ...props
}) => {
  const { register, formState, handleSubmit } = useForm({
    resolver: zodResolver(validationSchema || DefaultArticlePaywallFormValidationSchema),
  });

  return (
    <ArticlePaywallForm
      onSubmit={handleSubmit(async (values) => {
        await onSubmit?.(values);
      })}
      {...props}
    >
      <ArticlePaywallForm.Fieldset>
        {(textFields || defaultTextFields).map((textField, index) => (
          <ArticlePaywallForm.TextField
            key={index}
            caption={formState.errors[textField.name as string]?.message as string}
            label={textField.label}
            options={mergeOptions(
              { $input: { type: textField.type ?? 'text', ...register(textField.name as string) } },
              props?.options?.$textField,
            )}
            status={formState.errors[textField.name as string] ? 'error' : undefined}
          />
        ))}
        {(checkboxFields || []).map((checkboxField, index) => (
          <ArticlePaywallForm.CheckboxField
            key={index}
            caption={formState.errors[checkboxField.name as string]?.message as string}
            label={checkboxField.label}
            options={mergeOptions(
              { $input: { ...register(checkboxField.name as string) } },
              props?.options?.$checkboxField,
            )}
            status={formState.errors[checkboxField.name as string] ? 'error' : undefined}
          />
        ))}
      </ArticlePaywallForm.Fieldset>
      {policy && <ArticlePaywallForm.Policy>{policy}</ArticlePaywallForm.Policy>}
      <ArticlePaywallForm.Button {...mergeProps({ options: { type: 'submit', ...props.options?.$button } }, button)} />
      {caption && <ArticlePaywallForm.Caption>{caption}</ArticlePaywallForm.Caption>}
      {provider && <ArticlePaywallForm.Provider>{provider}</ArticlePaywallForm.Provider>}
    </ArticlePaywallForm>
  );
};
